import React, { memo, useMemo } from 'react';
import Edge from './Edge';
import { isTaskPending, isEdgeBlocking } from './utils';

// Re-export components for external use
export { default as ArrowheadDefs } from './ArrowheadDefs';
export { default as ConnectionPreview } from './ConnectionPreview';

/**
 * EdgeRenderer Component
 * 
 * Renders all edges (dependencies) between nodes.
 * 
 * PERFORMANCE OPTIMIZATIONS:
 * - Uses pre-cached pathD from edges (computed by useEdgePathCache)
 * - Pre-computes task lookup map (O(1) instead of O(n) per edge)
 * - Memoized to prevent re-renders during pan/zoom
 */
const EdgeRenderer = memo(({
  edges,
  selectedEdge,
  reconnectingEdgeId = null,
  tasks,
  onEdgeClick,
  onEdgeArrowDragStart,
}) => {
  // PERFORMANCE: Create task lookup map once (O(n) instead of O(n²))
  const taskMap = useMemo(() => {
    const map = {};
    tasks.forEach(t => { map[t.id] = t; });
    return map;
  }, [tasks]);
  
  // PERFORMANCE: Compute blocking/pending states only
  // pathD is already cached in the edge object from useEdgePathCache
  const edgeData = useMemo(() => {
    return edges.map(edge => {
      const fromTask = taskMap[edge.from];
      const toTask = taskMap[edge.to];
      
      return {
        edge,
        pathD: edge.pathD, // Use pre-cached path from useEdgePathCache
        isBlocking: isEdgeBlocking(fromTask, edge),
        isTargetPending: isTaskPending(toTask),
      };
    });
  }, [edges, taskMap]);
  
  return (
    <>
      {edgeData.map(({ edge, pathD, isBlocking, isTargetPending }) => (
        <Edge
          key={edge.id}
          edge={edge}
          pathD={pathD}
          isSelected={selectedEdge?.id === edge.id}
          isReconnecting={reconnectingEdgeId === edge.id}
          isBlocking={isBlocking}
          isTargetPending={isTargetPending}
          onEdgeClick={onEdgeClick}
          onEdgeArrowDragStart={onEdgeArrowDragStart}
          isFadingOut={edge.isFadingOut}
          isFadingIn={edge.isFadingIn}
        />
      ))}
    </>
  );
});

EdgeRenderer.displayName = 'EdgeRenderer';

export default EdgeRenderer;

